import { ProgramTypes } from 'legacy/lut/classes/models/ProgramTypes'
import { parseSectionSettings } from 'legacy/lut/features/partner360/configuration'
import type { IClientSectionSettings } from 'legacy/lut/features/partner360/sectionSettings/models/iSectionSettingsState'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import {
  selectCanonicalCockpitFinSobRows,
  selectCockpitActualsAndTargetsSettings,
  selectCockpitPartnerData,
  selectCockpitPendingActions,
  selectCockpitSobCollectingPeriod,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'

import {
  getMissingSobCategories,
  type IMissingSobCategoriesResult,
} from './getMissingSobCategories'

export interface IUseMissingSobCategoriesResult extends IMissingSobCategoriesResult {
  fYear: number
  /** The quarter SOB is collected for; `null` when the payload names no collecting cycle. */
  fQuarter: number | null
}

/**
 * What the `SOB Actuals are missing` card lists, read off the cockpit payload the tile already
 * holds — no second request, and no re-deciding of the status the server raised.
 *
 * The partner's flags and section settings come from `PartnerData` rather than from the planning
 * configuration the SOB modal fetches for itself: `planningCockpitGzip` carries both fields on the
 * same record (`PartnerFlags`, `SectionSettings`), so the catalog can be matched against the
 * partner without the card waiting on a request of its own.
 *
 * The period is the `jbp-sob-collecting` cycle of `PlanningCycles`, picked by its type: its own
 * year and quarter, the period the server names in the status (`FY26 Q4` while the cockpit already
 * stands on FY27). Without that cycle the year on screen is read whole, where a category counts as
 * answered as soon as any quarter of it carries a figure.
 */
export const useMissingSobCategories = (
  offering: ProgramTypes | null
): IUseMissingSobCategoriesResult => {
  const settings = useSelector(selectCockpitActualsAndTargetsSettings)
  const rows = useSelector(selectCanonicalCockpitFinSobRows)
  const pendingActions = useSelector(selectCockpitPendingActions)
  const partnerData = useSelector(selectCockpitPartnerData)
  const sobCollectingPeriod = useSelector(selectCockpitSobCollectingPeriod)
  const cockpitFYear = useSelector(selectEffectiveCockpitFYear)

  /* A payload fetched for one partner carries that partner's record and no other, which is the
     cockpit this card is opened from. */
  const partner = partnerData[0]

  const fYear = sobCollectingPeriod?.fYear ?? cockpitFYear
  const fQuarter = sobCollectingPeriod?.fQuarter ?? null

  const actions = useMemo(
    () =>
      pendingActions
        .filter(({ Offering }) => Offering === offering)
        .flatMap(({ PBMActions }) => PBMActions),
    [pendingActions, offering]
  )

  const sectionSettings = useMemo(
    () => parseSectionSettings(partner?.SectionSettings ?? null) as Partial<IClientSectionSettings>,
    [partner?.SectionSettings]
  )

  const result = useMemo(() => {
    if (!offering) {
      return { categories: [], state: 'noCatalog' } as IMissingSobCategoriesResult
    }

    return getMissingSobCategories({
      settings,
      rows,
      actions,
      offering,
      partnerFlags: partner?.PartnerFlags ?? '',
      sectionSettings,
      fYear,
      fQuarter,
    })
  }, [settings, rows, actions, offering, partner?.PartnerFlags, sectionSettings, fYear, fQuarter])

  return { ...result, fYear, fQuarter }
}
